import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { VaultData, VaultMeta, VaultItem, Folder } from '../config'

export interface UnlockedVault {
  id: string
  meta: VaultMeta
  data: VaultData
  key: Uint8Array
  verificationTag: Uint8Array
  isDirty: boolean
}

interface AppState {
  // User
  userEmail: string | null
  setUserEmail: (email: string | null) => void

  // Theme
  theme: 'light' | 'dark' | 'system'
  setTheme: (theme: 'light' | 'dark' | 'system') => void

  // Bookmarked vaults
  bookmarkedVaultIds: string[]
  setBookmarkedVaultIds: (ids: string[]) => void
  addBookmarkedVault: (id: string) => void
  removeBookmarkedVault: (id: string) => void

  // Currently unlocked vault (in memory only, not persisted)
  unlockedVault: UnlockedVault | null
  setUnlockedVault: (vault: UnlockedVault | null) => void
  updateVaultData: (updater: (data: VaultData) => VaultData) => void
  markVaultDirty: () => void
  markVaultClean: () => void

  // UI state
  selectedFolderId: string | null
  setSelectedFolderId: (id: string | null) => void
  selectedTagIds: string[]
  setSelectedTagIds: (ids: string[]) => void
  toggleTagFilter: (id: string) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
}

// Store with persistence for theme and bookmarks only
export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      // User
      userEmail: null,
      setUserEmail: (email) => set({ userEmail: email }),

      // Theme
      theme: 'system',
      setTheme: (theme) => set({ theme }),

      // Bookmarked vaults
      bookmarkedVaultIds: [],
      setBookmarkedVaultIds: (ids) => set({ bookmarkedVaultIds: ids }),
      addBookmarkedVault: (id) =>
        set((state) => ({
          bookmarkedVaultIds: state.bookmarkedVaultIds.includes(id)
            ? state.bookmarkedVaultIds
            : [...state.bookmarkedVaultIds, id],
        })),
      removeBookmarkedVault: (id) =>
        set((state) => ({
          bookmarkedVaultIds: state.bookmarkedVaultIds.filter((v) => v !== id),
        })),

      // Currently unlocked vault
      unlockedVault: null,
      setUnlockedVault: (vault) =>
        set({
          unlockedVault: vault,
          selectedFolderId: null,
          selectedTagIds: [],
          searchQuery: '',
        }),
      updateVaultData: (updater) =>
        set((state) => {
          if (!state.unlockedVault) return state
          return {
            unlockedVault: {
              ...state.unlockedVault,
              data: updater(state.unlockedVault.data),
              isDirty: true,
            },
          }
        }),
      markVaultDirty: () =>
        set((state) => {
          if (!state.unlockedVault) return state
          return {
            unlockedVault: { ...state.unlockedVault, isDirty: true },
          }
        }),
      markVaultClean: () =>
        set((state) => {
          if (!state.unlockedVault) return state
          return {
            unlockedVault: { ...state.unlockedVault, isDirty: false },
          }
        }),

      // UI state
      selectedFolderId: null,
      setSelectedFolderId: (id) => set({ selectedFolderId: id }),
      selectedTagIds: [],
      setSelectedTagIds: (ids) => set({ selectedTagIds: ids }),
      toggleTagFilter: (id) =>
        set((state) => ({
          selectedTagIds: state.selectedTagIds.includes(id)
            ? state.selectedTagIds.filter((t) => t !== id)
            : [...state.selectedTagIds, id],
        })),
      searchQuery: '',
      setSearchQuery: (query) => set({ searchQuery: query }),
      sidebarOpen: false,
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
    }),
    {
      name: 'vault-app-storage',
      partialize: (state) => ({
        theme: state.theme,
        bookmarkedVaultIds: state.bookmarkedVaultIds,
      }),
    }
  )
)

// Helper hooks for filtered items
export function useFilteredItems(): VaultItem[] {
  const vault = useAppStore((s) => s.unlockedVault)
  const selectedFolderId = useAppStore((s) => s.selectedFolderId)
  const selectedTagIds = useAppStore((s) => s.selectedTagIds)
  const searchQuery = useAppStore((s) => s.searchQuery)

  if (!vault) return []

  let items = vault.data.items

  // Filter by folder
  if (selectedFolderId !== null) {
    items = items.filter((item) => item.folderId === selectedFolderId)
  }

  // Filter by tags (AND logic)
  if (selectedTagIds.length > 0) {
    items = items.filter((item) =>
      selectedTagIds.every((tagId) => item.tagIds.includes(tagId))
    )
  }

  // Filter by search query
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase()
    items = items.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query) ||
        (item.description?.toLowerCase().includes(query) ?? false)
    )
  }

  // Sort by updated date (newest first)
  return [...items].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )
}

// Helper to get folder hierarchy
export function useFolderTree(): (Folder & { depth: number })[] {
  const vault = useAppStore((s) => s.unlockedVault)
  if (!vault) return []

  const folders = vault.data.folders
  const result: (Folder & { depth: number })[] = []

  function addFolder(folder: Folder, depth: number) {
    result.push({ ...folder, depth })
    const children = folders.filter((f) => f.parentId === folder.id)
    children.sort((a, b) => a.name.localeCompare(b.name))
    for (const child of children) {
      addFolder(child, depth + 1)
    }
  }

  const rootFolders = folders.filter((f) => f.parentId === null)
  rootFolders.sort((a, b) => a.name.localeCompare(b.name))
  for (const folder of rootFolders) {
    addFolder(folder, 0)
  }

  return result
}
